/**
 * The one set of display words for the console (rd Y0).
 *
 * Every Y lane imports these instead of writing a fifth account helper or a
 * second money formatter. Pure functions: no DOM, no page imports, no fetch.
 * The decided wordings and formats live in `docs/design/rd-polish-rules.md`;
 * do not invent a second wording here.
 *
 * Money is NOT re-implemented: `moneyText` parses with `decimalToCents` and
 * formats with `formatCents`, so the whole console keeps one exact-bigint
 * path (`src/ui/money.ts`, audit RC-6).
 */

import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import type { AccountPath, CompanySlug } from "../types.js";

import { decimalToCents, formatCents } from "./money.js";

/** Shown when a path carries no usable segment. Never the raw path. */
export const ACCOUNT_UNKNOWN_TEXT = "an account";

/** Shown when no company display name is known. Never the slug. */
export const COMPANY_UNKNOWN_TEXT = "this company";

const LEDGER_ROOTS: ReadonlySet<string> = new Set([
  "assets",
  "liabilities",
  "equity",
  "income",
  "expenses",
]);

/**
 * The singular spellings a ledger body may also carry. Only the `ledgerRoot`
 * drop rule honours them; the company-code rule keys on the real roots so a
 * leading `Asset:` is never mistaken for a company.
 */
const LEDGER_HEAD_WORDS: ReadonlySet<string> = new Set([
  ...LEDGER_ROOTS,
  "asset",
  "liability",
  "expense",
]);

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

function isLedgerRoot(segment: string): boolean {
  return LEDGER_ROOTS.has(segment.toLowerCase());
}

function isLedgerHeadWord(segment: string): boolean {
  return LEDGER_HEAD_WORDS.has(segment.toLowerCase());
}

/**
 * The company code this console puts at segment 2 (`Assets:GVPM:Chase:Operating`).
 * It is an internal key, never a word a person reads, so it is dropped from
 * the label the same way a leading company root is. An all-caps word with no
 * separator (`SYN`) is a code too; a capitalised word (`Trust`, `Chase`) is a
 * real account name and stays.
 */
function isCompanyCode(segment: string): boolean {
  return /^[A-Z][A-Z0-9]*$/.test(segment) && segment.length >= 2;
}

/** Split a colon path into trimmed, hazard-stripped, non-empty segments. */
function segmentsOf(path: string): string[] {
  return amVisibleDisplayText(path)
    .split(":")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/**
 * One segment as words: `_` and `-` separate words, a lower-to-upper case
 * change splits a camel-cased name, and the words after a camel split are
 * demoted to sentence case (`ProcessorFee` -> `Processor fee`). A segment
 * that was already words keeps the case it was written in
 * (`Opening-Balances` -> `Opening Balances`), as does an all-caps code
 * (`GVPM` -> `GVPM`).
 */
function segmentWords(segment: string): string {
  const separated = segment.replace(/[_-]+/g, " ").trim();
  const camelSplit = separated.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  const words = camelSplit.split(/\s+/).filter((word) => word.length > 0);
  const first = words[0];
  if (first === undefined) return "";
  if (camelSplit === separated) return words.join(" ");
  return [first, ...words.slice(1).map((word) => word.toLowerCase())].join(" ");
}

/**
 * The one thing every account label needs: each company's ledger segment ->
 * that company's plain name (`SYNT` -> `Test Street Trust`), from the shell's
 * company directory (`face.accountLabelContext`). rd13 L1.
 *
 * WHY IT IS A REQUIRED ARGUMENT, NOT AN OPTION. The segment is an internal
 * key. While naming it was optional, every page that forgot it printed
 * "Due from TRST" and "Repairs (RENT)" on a main screen; an optional parameter
 * is forgotten exactly where nobody looks. A page builds this map ONCE from
 * the directory it already holds and hands it to every label call, so a new
 * call cannot compile without one.
 *
 * A segment the map does not know reads `another company` (never the key):
 * the shell's directory is built from the whole company read, so an unknown
 * segment is not a company we can name.
 */
export type AccountLabelContext = ReadonlyMap<string, string>;

/**
 * No company names known. For a test, and for the one kind of caller that has
 * no directory at all: a label built inside the label-set builders themselves.
 * A page must never pass this — `account-label-guard.test.ts` fails on it.
 */
export const NO_COMPANY_NAMES: AccountLabelContext = new Map();

export interface AccountLabelStyle {
  /** Between segments. Default ` › `; the ledger body uses ` · `. */
  separator?: string;
  /**
   * Which leading segment is dropped before joining. Default `companyCode`
   * (rd13 L1: a balance-sheet head is dropped too, so the label is the
   * account's name); `ledgerRoot` always drops the Assets/Expenses head
   * (`Expenses:Repairs` -> `Repairs`), for a page that already sits under it.
   */
  dropLeading?: "companyCode" | "ledgerRoot";
}

/**
 * A ledger head that names a CATEGORY a bookkeeper files an account under.
 * Income and Expenses keep it ("Expenses › Repairs"); Assets, Liabilities and
 * Equity name a report section, not the account, so their head is dropped
 * ("Assets:Checking:Operating" -> "Checking › Operating") — rd13 L1.
 */
const CATEGORY_HEADS: ReadonlySet<string> = new Set(["income", "expenses"]);

/**
 * The words a between-companies account uses for its counterparty
 * (`pipeline/intercompany.py` `IC_ACCOUNT_PATTERN`:
 * `Assets:<A>:Due-From:<B>` / `Liabilities:<B>:Due-To:<A>`). The relation
 * leads and the company follows, which is how a bookkeeper writes it.
 */
const RELATION_WORDS: ReadonlyMap<string, string> = new Map([
  ["due-from", "Due from"],
  ["due-to", "Due to"],
  ["receivable-from", "Receivable from"],
  ["payable-to", "Payable to"],
]);

/** Shown for a counterparty the caller's directory cannot name (never its key). */
export const ACCOUNT_OTHER_COMPANY_TEXT = "another company";

function relationWords(segment: string): string | undefined {
  return RELATION_WORDS.get(segment.toLowerCase().replace(/[_\s]+/g, "-"));
}

function isCategoryHead(segment: string): boolean {
  return CATEGORY_HEADS.has(segment.toLowerCase());
}

/**
 * The readable name for an account path. Stored and submitted values keep the
 * full colon name; only the label changes: `Expenses:Repairs` ->
 * `Expenses › Repairs`, `Expenses:ProcessorFee` -> `Expenses › Processor fee`,
 * `Assets:Checking:Operating` -> `Checking › Operating`,
 * `Assets:GVPM:Due-From:GVFO` -> `Due from GVFO`.
 *
 * rd13 L1 — THE ONE NAMING RULE, so no page invents a second one:
 *   - a category account keeps its group (Income / Expenses);
 *   - a balance-sheet account drops the head, because the section, the page
 *     or the picker's grouping already says which side of the books it is;
 *   - the company code is an internal key and is never a word;
 *   - a between-companies account reads "<relation> <company>".
 *
 * A path with no usable segment returns `ACCOUNT_UNKNOWN_TEXT`, so a caller
 * can never print a colon path or an empty label.
 */
export function accountLabel(
  path: string,
  companies: AccountLabelContext,
  style: AccountLabelStyle = {},
): string {
  // The strip happens here, on the path itself; `labelPieces` only ever sees
  // the stripped segments.
  const pieces = labelPieces(segmentsOf(path), style);
  if (pieces === null) return ACCOUNT_UNKNOWN_TEXT;
  if (pieces.party !== undefined) {
    const relation = pieces.relation ?? "";
    const party = partyWords(pieces.party, companies);
    const named = [relation, party].filter((word) => word.length > 0).join(" ");
    if (named.length > 0) return named;
  }
  const label = pieces.shown
    .map(segmentWords)
    .filter((word) => word.length > 0)
    .join(style.separator ?? " › ");
  return label.length > 0 ? label : ACCOUNT_UNKNOWN_TEXT;
}

/**
 * The counterparty of a between-companies account: its company's plain name,
 * else the honest stand-in. Never the internal key (rd13 L1).
 */
function partyWords(segment: string, companies: AccountLabelContext): string {
  const known = companies.get(segment)?.trim() ?? "";
  return known !== "" ? known : ACCOUNT_OTHER_COMPANY_TEXT;
}

/**
 * The segments a label reads (`shown`) and the ones it leaves out (`dropped`:
 * a leading company root, the company code, or a balance-sheet head). A
 * between-companies account also carries its `relation` and `party`, which
 * `accountLabel` words in sentence order. The label-set disambiguator below
 * names `dropped` when two accounts would otherwise read the same.
 */
function labelPieces(
  parts: readonly string[],
  style: AccountLabelStyle,
): { shown: string[]; dropped: string[]; relation?: string; party?: string } | null {
  if (parts.length === 0) return null;
  const drop = style.dropLeading ?? "companyCode";
  const first = parts[0] ?? "";
  const second = parts[1] ?? "";
  const dropIndex =
    drop === "ledgerRoot"
      ? isLedgerHeadWord(first)
        ? 1
        : 0
      : !isLedgerRoot(first) && isLedgerRoot(second)
        ? 1
        : 0;
  const dropped = parts.slice(0, dropIndex);
  let kept = parts.slice(dropIndex);
  // The ledger head: a category keeps it; a balance-sheet section name is not
  // part of the account's own name, so it goes (never the only segment).
  const head = kept[0] ?? "";
  let headKept = false;
  if (kept.length > 1 && isLedgerHeadWord(head) && (drop === "ledgerRoot" || !isCategoryHead(head))) {
    dropped.push(head);
    kept = kept.slice(1);
  } else if (isLedgerHeadWord(head)) {
    headKept = true;
  }
  // The company code sits right after the ledger head: kept[1] when the head is
  // kept, kept[0] once it is dropped (`Assets:GVPM:Chase:Operating`,
  // `Expenses:SYN:Rent`). It is an internal key, not a word, so it goes. There
  // is always another segment left to read.
  const codeAt = headKept ? 1 : 0;
  const candidate = kept[codeAt];
  if (candidate !== undefined && kept.length > codeAt + 1 && isCompanyCode(candidate)) {
    kept = [...kept.slice(0, codeAt), ...kept.slice(codeAt + 1)];
    dropped.push(candidate);
  }
  // Dropping the only segment would leave nothing to read; show it instead.
  if (kept.length === 0) return { shown: [...parts], dropped: [] };
  // A between-companies account: "<relation>:<company>". The relation leads.
  if (kept.length === 2) {
    const relation = relationWords(kept[0] ?? "");
    const party = kept[1] ?? "";
    if (relation !== undefined && party.length > 0) {
      return { shown: kept, dropped, relation, party };
    }
  }
  return { shown: kept, dropped };
}

/**
 * Labels for a SET of accounts offered together (a chooser, a "Book to" list).
 * `accountLabel` drops the company code, so `Expenses:Repairs` and
 * `Expenses:RENT:Repairs` both read `Expenses › Repairs`, and a person picking
 * between them cannot tell which is which (integration re-check E6). Where two
 * or more accounts in the set share a label, each gets the segment its label
 * dropped appended — the company's plain name from `companies` (rd13 L1). An
 * account whose label is unique is unchanged, and so is the stored path every
 * caller still submits.
 *
 * rd13 L1 — A COLLISION THAT CROSSES THE SIDE OF THE BOOKS SAYS THE SIDE.
 * Dropping the balance-sheet head made `Assets:Deposits` and
 * `Liabilities:Deposits` both read `Deposits`, and a number ("Deposits (1)",
 * "Deposits (2)") does not say which side of the books it is on. When the
 * colliding accounts do not all sit under one ledger head, each says its side
 * in plain words: `Deposits (asset)` / `Deposits (liability)`, `Opening
 * (equity)`. When they do sit under one head (the ordinary case) the head is
 * still not a parenthetical, because the section, the page or the picker
 * already says it, and the shorter label stays. If labels still collide, a
 * number tells them apart.
 */
export function accountLabelSet(
  accounts: readonly string[],
  companies: AccountLabelContext,
  style: AccountLabelStyle = {},
): ReadonlyMap<string, string> {
  const unique = [...new Set(accounts)];
  const labels = new Map<string, string>();
  const groups = new Map<string, string[]>();
  for (const account of unique) {
    const label = accountLabel(account, companies, style);
    labels.set(account, label);
    // Two labels that differ only in case or a separator are the SAME words to
    // a reader (`Processor fee` / `Processor Fee`), so they group like exact
    // twins and are told apart below (round 10, A9-M2).
    const key = labelFoldKey(label);
    groups.set(key, [...(groups.get(key) ?? []), account]);
  }
  for (const [groupKey, members] of groups) {
    if (members.length < 2) continue;
    // Which side of the books each member sits on. More than one side in one
    // colliding group is a collision no segment can explain, so the side is
    // the word that tells them apart (round 13, review item 2).
    const sides = new Set(
      members
        .map((account) => ledgerHead(account))
        .filter((head): head is string => head !== null),
    );
    const crossHead = sides.size > 1;
    for (const account of members) {
      const shown = labels.get(account) ?? ACCOUNT_UNKNOWN_TEXT;
      const dropped = labelPieces(segmentsOf(account), style)?.dropped ?? [];
      const parts: string[] = [];
      if (crossHead) {
        const side = sideWords(ledgerHead(account));
        if (side !== "") parts.push(side);
      }
      for (const segment of dropped) {
        // A balance-sheet head is dropped because the page says it, not
        // because it tells two accounts apart, so it is never a parenthetical
        // (unless it is the only word that can, above).
        if (isLedgerHeadWord(segment)) continue;
        const words = droppedWords(segment, companies);
        if (words !== "") parts.push(words);
      }
      const extra = parts.join(" › ");
      labels.set(account, extra === "" ? shown : `${shown} (${extra})`);
    }
    // Labels that still collide (same dropped segment, or nothing dropped on
    // either) are numbered in stored-name order so the list stays stable. The
    // comparison folds case and separators for the same reason as the group.
    const again = new Map<string, string[]>();
    for (const account of [...members].sort()) {
      const text = labels.get(account) ?? groupKey;
      const key = labelFoldKey(text);
      again.set(key, [...(again.get(key) ?? []), account]);
    }
    for (const same of again.values()) {
      if (same.length < 2) continue;
      same.forEach((account, index) => {
        const text = labels.get(account) ?? groupKey;
        labels.set(account, `${text} (${String(index + 1)})`);
      });
    }
  }
  return labels;
}

/** The ledger head an account sits under (`Assets`, `Liabilities`, ...), or null. */
function ledgerHead(path: string): string | null {
  for (const segment of segmentsOf(path)) {
    if (isLedgerRoot(segment)) return segment.toLowerCase();
  }
  return null;
}

/**
 * How a side of the books is said in a picker: `Deposits (asset)` /
 * `Deposits (liability)`. A single word rather than a sentence, because it
 * sits inside a label; the singular, because it names one account's side.
 * `owed to us` / `we owe` was the other candidate and was rejected: it is
 * true of a receivable and a payable but reads wrong on bank deposits and
 * equity, which is most of the collisions this fixes.
 */
const SIDE_WORDS: ReadonlyMap<string, string> = new Map([
  ["assets", "asset"],
  ["liabilities", "liability"],
  ["equity", "equity"],
  ["income", "income"],
  ["expenses", "expense"],
]);

function sideWords(head: string | null): string {
  if (head === null) return "";
  return SIDE_WORDS.get(head) ?? "";
}

/** Case and separator fold for "these read the same" (round 10, A9-M2). */
function labelFoldKey(label: string): string {
  return label.toLowerCase().replace(/[\s_-]+/g, "");
}

/**
 * One dropped segment as the words a parenthetical may carry.
 *
 * A company code reads as the company's plain name. A code the directory does
 * not know reads `another company`, never the internal key (rd13 L1): the
 * shell's directory is the whole company read, so an unknown segment is not a
 * company we can name, and a code is not a word a bookkeeper reads.
 */
function droppedWords(segment: string, companies: AccountLabelContext): string {
  if (isCompanyCode(segment)) {
    const known = companies.get(segment)?.trim() ?? "";
    return known !== "" ? known : ACCOUNT_OTHER_COMPANY_TEXT;
  }
  return segmentWords(segment);
}

/**
 * One account's label among the accounts offered with it (`accountLabelSet`),
 * for a sentence that names a choice. An account that is not in the set reads
 * as it always did.
 */
export function accountLabelAmong(
  path: string,
  offered: readonly string[],
  companies: AccountLabelContext,
  style: AccountLabelStyle = {},
): string {
  if (!offered.includes(path)) return accountLabel(path, companies, style);
  return accountLabelSet(offered, companies, style).get(path) ?? accountLabel(path, companies, style);
}

/**
 * One account's label beside the accounts a company offers, for an account
 * that may not be offered yet: a request to OPEN an account names a path the
 * chart does not hold, and a request to close one names a path the chart
 * holds. Either way the label is taken as if the account were in the set, so
 * a new `Expenses:RENT:Repairs` is told apart from the existing
 * `Expenses:Repairs` in the sentence the person approves (integration review
 * G3).
 */
export function accountLabelBeside(
  path: string,
  offered: readonly string[],
  companies: AccountLabelContext,
  style: AccountLabelStyle = {},
): string {
  return accountLabelAmong(
    path,
    offered.includes(path) ? offered : [...offered, path],
    companies,
    style,
  );
}

/**
 * Account labels per company, for every surface that names an account the
 * person may then book to (round five F2). Built once per company from the
 * accounts that company offers, so two accounts that read the same are told
 * apart by the same words wherever they are named: the chooser, the Book-to
 * buttons, the card face, the suggestion list and a booked answer.
 *
 * - `label(company, account)`: always a label, the set label when the company
 *   offers the account, else the plain `accountLabel`.
 * - `whenShared(company, account)`: the set label ONLY for an account that
 *   another account of the company is a twin of: its whole plain label, or just
 *   its last words, read the same (`Income:Rent` and `Expenses:Rent` both end in
 *   "Rent", and a card prints only that). Both twins get the full label, the one
 *   that owns the plain words too; null otherwise, so a caller that words a
 *   unique account its own shorter way keeps doing so. This is THE twin
 *   decision: every surface that prints a leaf or short label for an account
 *   that can be booked asks it (round eight A-H1; the account-label guard
 *   enforces it).
 *
 * `offeredFor` is read at most once per company.
 */
export interface CompanyAccountLabels {
  label: (company: CompanySlug, account: AccountPath) => string;
  whenShared: (company: CompanySlug, account: AccountPath) => string | null;
}

export function companyAccountLabels(
  offeredFor: (company: CompanySlug) => readonly AccountPath[],
  companies: AccountLabelContext,
): CompanyAccountLabels {
  interface Built {
    labels: ReadonlyMap<string, string>;
    /** Accounts whose plain words another account of the company also has. */
    shared: ReadonlySet<string>;
  }
  const built = new Map<CompanySlug, Built>();
  const buildFor = (company: CompanySlug): Built => {
    const known = built.get(company);
    if (known !== undefined) return known;
    const offered = [...new Set(offeredFor(company))];
    // Two accounts are twins when ANY wording a surface prints for them is the
    // same: the whole label (`Expenses › Repairs` for two companies' accounts)
    // or just the last words (`Income:Rent` and `Expenses:Rent` both end in
    // "Rent", and a card prints only that). Round eight A-H1: comparing whole
    // labels alone let two cards read "Yes, book to Rent" and post to income
    // on one and an expense on the other.
    const byWords = new Map<string, AccountPath[]>();
    const remember = (key: string, account: AccountPath): void => {
      byWords.set(key, [...(byWords.get(key) ?? []), account]);
    };
    for (const account of offered) {
      const label = accountLabel(account, companies);
      remember(`label:${labelFoldKey(label)}`, account);
      remember(`leaf:${leafWordsKey(account, companies)}`, account);
    }
    const shared = new Set<AccountPath>();
    for (const members of byWords.values()) {
      if (members.length > 1) for (const account of members) shared.add(account);
    }
    const made: Built = { labels: accountLabelSet(offered, companies), shared };
    built.set(company, made);
    return made;
  };
  return {
    label: (company, account) =>
      buildFor(company).labels.get(account) ?? accountLabel(account, companies),
    // Both twins are named in full, the one that owns the plain words too: a
    // short "Repairs" beside "Expenses \u203a Repairs (Test Street Rentals)"
    // would not say which.
    whenShared: (company, account) => {
      const made = buildFor(company);
      return made.shared.has(account) ? (made.labels.get(account) ?? null) : null;
    },
  };
}

/**
 * The last segment's words with case and separators folded away, so
 * `ProcessorFee`, `Processor-Fee` and `Processor fee` count as one wording.
 * The comparison key of the twin decision (`companyAccountLabels`); never shown.
 */
function leafWordsKey(path: AccountPath, companies: AccountLabelContext): string {
  return accountLeafLabel(path, companies).toLowerCase().replace(/[\s_-]+/g, "");
}

/**
 * The last segment as words, for places that already sit under a heading
 * (`Repairs`, `Processor fee`, `Late fee`).
 *
 * A between-companies account has no leaf a person can read — its last
 * segment is the counterparty's internal key — so it reads the whole label
 * (`Due from Test Street Trust`). rd13 L1: a card that says "Yes, book to
 * TRST" names an account by a key nobody can look up.
 */
export function accountLeafLabel(
  path: string,
  companies: AccountLabelContext,
): string {
  const pieces = labelPieces(segmentsOf(path), {});
  if (pieces !== null && pieces.party !== undefined) {
    return accountLabel(path, companies);
  }
  const parts = segmentsOf(path);
  const last = parts[parts.length - 1];
  if (last === undefined) return ACCOUNT_UNKNOWN_TEXT;
  const label = segmentWords(last);
  return label.length > 0 ? label : ACCOUNT_UNKNOWN_TEXT;
}

/**
 * The books' between-companies shape, as ONE rule: an account whose label is
 * "Due from/To <company>" (`Assets:<A>:Due-From:<B>`,
 * `Liabilities:<B>:Due-To:<A>` — `pipeline/intercompany.py`
 * `IC_ACCOUNT_PATTERN`). Money that moves between two of our companies is not
 * a bank account and never receives a bank statement; a page that must tell
 * those apart asks here rather than carrying its own copy of the pattern.
 */
export function isBetweenCompaniesAccount(path: string): boolean {
  return labelPieces(segmentsOf(path), {})?.party !== undefined;
}

/**
 * A contract-supplied line label, with the owner's decided wordings applied.
 * The pipeline still writes `Moov fee` on the processor-fee line
 * (`pipeline/ledger_view.py`), and the decided word for that account is
 * `Processor fee` (`docs/design/rd-polish-rules.md` §1). Only exact, known
 * superseded spellings are replaced; every other contract label is shown as
 * written.
 */
const SUPERSEDED_LINE_LABELS: ReadonlyMap<string, string> = new Map([
  ["moov fee", "Processor fee"],
  ["moov fees", "Processor fee"],
]);

export function lineLabelText(label: string | null | undefined): string {
  const text = amVisibleDisplayText(label ?? "").trim();
  if (text === "") return "";
  return SUPERSEDED_LINE_LABELS.get(text.toLowerCase()) ?? text;
}

export interface MoneyTextOptions {
  /** Shown for a missing or malformed amount. Default `""`. */
  fallback?: string;
}

const DECIMAL_TEXT = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/;

/**
 * A contract decimal amount as money on screen: always `$1,234.56`, negative
 * `−$0.40`, never `549.60`, `42.00 USD` or `6171.80`. A malformed amount
 * returns the caller's fallback rather than `NaN` or a raw server string.
 */
export function moneyText(
  amount: string | null | undefined,
  options: MoneyTextOptions = {},
): string {
  const fallback = options.fallback ?? "";
  if (amount === null || amount === undefined) return fallback;
  let text = amVisibleDisplayText(amount).trim();
  if (text === "") return fallback;
  // The no-currency-suffix rule: "42.00 USD" reads "$42.00".
  text = text.replace(/^([+-]?[\d.,]+)\s*[A-Za-z]{2,3}$/, "$1");
  // A leading dollar sign is presentation, not part of the number.
  text = text.replace(/^([+-]?)\$/, "$1");
  text = text.replace(/[,\s_]/g, "");
  if (!DECIMAL_TEXT.test(text)) return fallback;
  const cents = decimalToCents(text.startsWith("+") ? text.slice(1) : text);
  if (cents === null) return fallback;
  return formatCents(cents);
}

const ISO_PARTS =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?(?:Z|([+-])(\d{2}):?(\d{2}))?$/;

/** A value with no time part: a calendar day on the wire, not an instant. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function utcDate(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
): Date | null {
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute));
  // Reject a rolled-over civil date (2026-02-31 must not become March 3).
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day ||
    date.getUTCHours() !== hour ||
    date.getUTCMinutes() !== minute
  ) {
    return null;
  }
  return date;
}

/**
 * Parse a contract date or timestamp. A value without a zone is read as UTC:
 * the console's contract timestamps are UTC and a local-time read would move
 * a date across midnight on the operator's machine.
 */
function instantOf(iso: string | null | undefined): Date | null {
  if (iso === null || iso === undefined) return null;
  const text = amVisibleDisplayText(iso).trim();
  const match = ISO_PARTS.exec(text);
  if (match === null) return null;
  const [, year, month, day, hour, minute, second, , sign, offsetHour, offsetMinute] =
    match;
  if (year === undefined || month === undefined || day === undefined) return null;
  const base = utcDate(
    Number(year),
    Number(month),
    Number(day),
    hour === undefined ? 0 : Number(hour),
    minute === undefined ? 0 : Number(minute),
  );
  if (base === null) return null;
  let millis = base.getTime();
  if (second !== undefined) millis += Number(second) * 1000;
  if (sign !== undefined) {
    const offset = (Number(offsetHour) * 60 + Number(offsetMinute)) * 60_000;
    millis += sign === "+" ? -offset : offset;
  }
  return new Date(millis);
}

/** `2026-10-16` -> `Oct 16, 2026`. Invalid input returns `""`. */
export function dateText(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return "";
  const text = amVisibleDisplayText(iso).trim();
  const date = instantOf(text);
  if (date === null) return "";
  // rd r12 A5-6: one dispatch, on the SHAPE of the value.
  // A date-only wire value (`YYYY-MM-DD`) is a calendar day, not an instant:
  // the day the API sent is the day shown, in every time zone.
  if (DATE_ONLY.test(text)) {
    const month = SHORT_MONTHS[date.getUTCMonth()];
    if (month === undefined) return "";
    return `${month} ${String(date.getUTCDate())}, ${String(date.getUTCFullYear())}`;
  }
  // A timestamp is an instant: it reads as the reader's own calendar day, the
  // same local clock `clockText` and `timeAgoText` use. Reading it in UTC put
  // an evening timestamp on the wrong day west of Greenwich.
  const month = SHORT_MONTHS[date.getMonth()];
  if (month === undefined) return "";
  return `${month} ${String(date.getDate())}, ${String(date.getFullYear())}`;
}

/**
 * Today as a date-only wire value (`YYYY-MM-DD`), never later than the Worker's
 * UTC today. rd r12 A5-6, round-11 final report item 25.
 *
 * The escrow date input's `max` and the future-date refusal are compared
 * against the Worker's own UTC day (`ingest-worker/src/admin.ts`). A local
 * "today" east of Greenwich is the future to the Worker on the 1st, so a person
 * could pick a date the Worker refuses. The earlier of the two is the one both
 * agree on; behind UTC (US evenings at month end) the local day is already the
 * earlier one and is used as is. Same shape as `periodNotAfterUtc`
 * (`ui/period-control.ts`), which does this for the `YYYY-MM` period.
 */
export function localTodayIsoDate(now: Date = new Date()): string {
  const local = `${String(now.getFullYear())}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  const utc = `${String(now.getUTCFullYear())}-${pad2(now.getUTCMonth() + 1)}-${pad2(now.getUTCDate())}`;
  return local < utc ? local : utc;
}

/** `2026-10` -> `October 2026`. Invalid input returns `""`. */
export function monthText(period: string | null | undefined): string {
  if (period === null || period === undefined) return "";
  const match = /^(\d{4})-(\d{2})(?:-\d{2})?/.exec(
    amVisibleDisplayText(period).trim(),
  );
  if (match === null) return "";
  const year = Number(match[1]);
  const month = MONTH_NAMES[Number(match[2]) - 1];
  if (month === undefined) return "";
  return `${month} ${String(year)}`;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

function clockText(date: Date): string {
  // Round 2 (5): the clock a person reads is the browser's local one, the same
  // zone freshness.ts uses. A UTC clock put an evening timestamp on the wrong
  // day for a viewer west of Greenwich.
  const month = SHORT_MONTHS[date.getMonth()];
  if (month === undefined) return "";
  const hours = date.getHours();
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  const meridiem = hours < 12 ? "AM" : "PM";
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${month} ${String(date.getDate())}, ${String(hour12)}:${minutes} ${meridiem}`;
}

/**
 * A plain phrase for when something happened: `just now`, `5 minutes ago`,
 * `3 hours ago`, `yesterday`, else `Oct 24, 12:00 PM`. Invalid input returns
 * `""`; a time in the future is shown as an absolute time, never as `NaN`.
 */
export function timeAgoText(
  iso: string | null | undefined,
  now: Date = new Date(),
): string {
  const then = instantOf(iso);
  if (then === null) return "";
  const elapsed = now.getTime() - then.getTime();
  if (elapsed < 0) return clockText(then);
  if (elapsed < MINUTE) return "just now";
  if (elapsed < HOUR) {
    const minutes = Math.floor(elapsed / MINUTE);
    return `${String(minutes)} ${minutes === 1 ? "minute" : "minutes"} ago`;
  }
  if (elapsed < 24 * HOUR) {
    const hours = Math.floor(elapsed / HOUR);
    return `${String(hours)} ${hours === 1 ? "hour" : "hours"} ago`;
  }
  const yesterday = new Date(now.getTime() - 24 * HOUR);
  if (
    yesterday.getFullYear() === then.getFullYear() &&
    yesterday.getMonth() === then.getMonth() &&
    yesterday.getDate() === then.getDate()
  ) {
    return "yesterday";
  }
  return clockText(then);
}

/**
 * `1 entry` / `2 entries` / `0 entries`. A count that is not a finite number
 * returns `""` so a caller can decide, rather than printing `NaN entries`.
 */
export function pluralText(
  count: number,
  singular: string,
  plural: string,
): string {
  if (!Number.isFinite(count)) return "";
  const whole = Math.trunc(count);
  const number = whole.toLocaleString("en-US");
  return `${number} ${whole === 1 ? singular : plural}`;
}

/**
 * A count read from a list the Worker may have capped, as a tile or badge
 * reads it: `12`, or `500+` when the list may hold more than it returned.
 * `complete` is the caller's honest answer to "did this read see everything?"
 * (cap not fired, every part of a fan-out settled). Every count printed from
 * a server list goes through this or `countOrMoreText`, so a screen cannot
 * print a capped number as exact without saying so on the page; the
 * count-guard test enforces it (integration review G2).
 */
export function countText(count: number, complete: boolean): string {
  const number = Math.trunc(count).toLocaleString("en-US");
  return complete ? number : `${number}+`;
}

/** The same count in words, for a sentence or a toggle: `12`, or `500 or more`. */
export function countOrMoreText(count: number, complete: boolean): string {
  const number = Math.trunc(count).toLocaleString("en-US");
  return complete ? number : `${number} or more`;
}

/**
 * The tail of "A and 3 more": `3 more`, or just `more` when the list the
 * count came from was cut short and the real number is not known.
 */
export function moreText(count: number, complete: boolean): string {
  return complete ? `${Math.trunc(count).toLocaleString("en-US")} more` : "more";
}

/**
 * `1 card`, `12 cards`, or `500 or more cards`. A lower bound is always
 * plural: "1 or more card" reads wrong, and a bound of one means the list is
 * cut at one, which is not a real cap.
 */
export function countedText(
  count: number,
  complete: boolean,
  singular: string,
  plural: string,
): string {
  if (!Number.isFinite(count)) return "";
  if (complete) return pluralText(count, singular, plural);
  return `${countOrMoreText(count, false)} ${plural}`;
}

const SLUG_TEXT = /^[a-z0-9]+(?:[-_.][a-z0-9]+)*$/;

/**
 * An internal key, not a name. A key is a single lowercase token
 * (`syn-rentals`), or the same shape in capitals (`SYN-RENTALS`) — the shell's
 * short label is literally `slug.toUpperCase()` (`main.ts:shortCompanyLabel`),
 * so an all-caps multi-word code has to be rejected too. A single capitalised
 * or all-capital word (`Crestwood`, `SYN`) is kept: Y0 pinned that, and a
 * one-word name is not a key.
 */
function looksLikeSlug(text: string): boolean {
  if (SLUG_TEXT.test(text)) return true;
  if (!SLUG_TEXT.test(text.toLowerCase())) return false;
  // Same shape in caps, and more than one segment: `SYN-RENTALS`, not `SYN`.
  return text === text.toUpperCase() && /[-_.]/.test(text);
}

/**
 * A company as a person reads it: the display name when known, never the
 * slug (`syn-rentals` -> `this company`). A value that already reads as a
 * name (a capital, or words with spaces) is kept; an unknown company falls
 * back to `this company` so no screen ever shows an internal key.
 *
 * A "known" name that is only the slug in another case (`gvpm` -> `GVPM`, which
 * is exactly what the shell's short label is) is not a name either.
 */
export function companyNameText(
  slugOrCompany: string | null | undefined,
  nameLookup?: ReadonlyMap<string, string>,
): string {
  const raw = amVisibleDisplayText(slugOrCompany ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (raw === "") return COMPANY_UNKNOWN_TEXT;
  // The looked-up display name is staff-entered text from the API: strip the
  // line hazards (bidi override, separators) like every other AM-visible name.
  const known = amVisibleDisplayText(nameLookup?.get(raw) ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (known !== "" && !looksLikeSlug(known)) {
    // The shell's short label for a short slug is literally `slug.toUpperCase()`
    // (`main.ts:shortCompanyLabel`), so that exact shape is a key. A real name
    // that merely matches the slug ignoring case ("Acme" for `acme`) is a name
    // and is kept — suppressing it made two such companies indistinguishable.
    const shellShortLabel = raw.toUpperCase();
    if (known !== shellShortLabel) return known;
  }
  // A name has letters; punctuation alone is a key, not a name.
  if (!/[A-Za-z]/.test(raw)) return COMPANY_UNKNOWN_TEXT;
  // The never-a-slug guard: an internal key is not a name.
  if (looksLikeSlug(raw)) return COMPANY_UNKNOWN_TEXT;
  return raw;
}
